import React from 'react';
import { CheckCircle, Target, Heart, Shield } from 'lucide-react';
import { companyInfo, aboutImages } from '../data/mock';

const Sobre = () => {
  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="relative pt-32 pb-20 bg-gradient-to-br from-[#F8E6C6] to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto">
            <span className="text-[#D7AC78] font-semibold text-sm uppercase tracking-wider mb-4 block">
              About Us
            </span>
            <h1 className="text-5xl md:text-6xl font-bold text-[#484237] mb-6 leading-tight">
              Excellence in Every Detail
            </h1>
            <p className="text-xl text-[#728481] leading-relaxed">
              {companyInfo.description}
            </p>
          </div>
        </div>
      </section>

      {/* Mission Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div className="relative">
              <div className="rounded-2xl overflow-hidden" style={{ 
                background: '#484237',
                boxShadow: '0 20px 60px rgba(72, 66, 55, 0.3)',
                minHeight: '500px'
              }}>
                <img
                  src="https://customer-assets.emergentagent.com/job_home-improvement-pro/artifacts/hyexmljb_Massimo_logo%206.png"
                  alt="Massimo Logo"
                  className="w-full h-full object-contain p-12"
                />
              </div>
              <div className="absolute -bottom-6 -left-6 bg-[#D7AC78] text-white p-8 rounded-xl shadow-2xl">
                <div className="text-4xl font-bold">50+</div>
                <div className="text-sm">Completed Projects</div>
              </div>
            </div>

            <div>
              <div className="flex items-center space-x-3 mb-6">
                <div className="w-12 h-12 bg-[#D7AC78]/10 rounded-full flex items-center justify-center">
                  <Target className="text-[#D7AC78]" size={24} />
                </div>
                <h2 className="text-3xl font-bold text-[#484237]">Our Mission</h2>
              </div>
              <p className="text-lg text-[#728481] mb-8 leading-relaxed">
                {companyInfo.mission}
              </p>
              <p className="text-lg text-[#728481] leading-relaxed">
                With over a year of experience, we have built a solid reputation based on 
                quality, transparency, and commitment to our clients' satisfaction.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-20 bg-[#F8E6C6]/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <div className="flex items-center justify-center space-x-3 mb-4">
              <div className="w-12 h-12 bg-[#D7AC78]/10 rounded-full flex items-center justify-center">
                <Heart className="text-[#D7AC78]" size={24} />
              </div>
              <h2 className="text-4xl font-bold text-[#484237]">Our Values</h2>
            </div>
            <p className="text-lg text-[#728481] max-w-2xl mx-auto">
              Principles that guide every project and decision
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {companyInfo.values.map((value, index) => (
              <div
                key={index}
                className="bg-white p-6 rounded-xl shadow-md hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1"
              >
                <div className="flex items-start space-x-4">
                  <CheckCircle className="text-[#D7AC78] flex-shrink-0 mt-1" size={24} />
                  <span className="text-lg font-semibold text-[#484237]">{value}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <div className="flex items-center justify-center space-x-3 mb-4">
              <div className="w-12 h-12 bg-[#D7AC78]/10 rounded-full flex items-center justify-center">
                <Shield className="text-[#D7AC78]" size={24} />
              </div>
              <h2 className="text-4xl font-bold text-[#484237]">Why Choose Massimo?</h2>
            </div>
            <p className="text-lg text-[#728481] max-w-2xl mx-auto">
              Differentiators that make us a renovation leader
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              {
                title: "Proven Experience",
                description: "Over a year transforming spaces with excellence",
                stat: "50+ projects"
              },
              {
                title: "Qualified Team",
                description: "Specialized and certified professionals",
                stat: "30+ experts"
              },
              {
                title: "Guaranteed Satisfaction",
                description: "Commitment to quality and final results",
                stat: "98% satisfaction"
              },
              {
                title: "Transparent Processes",
                description: "Clear communication at all project stages",
                stat: "100% transparency"
              }
            ].map((item, index) => (
              <div
                key={index}
                className="text-center p-6 bg-gradient-to-br from-[#F8E6C6]/50 to-white rounded-xl border border-[#D7AC78]/20 hover:border-[#D7AC78] transition-all duration-300"
              >
                <div className="text-3xl font-bold text-[#D7AC78] mb-3">{item.stat}</div>
                <h3 className="text-xl font-bold text-[#484237] mb-2">{item.title}</h3>
                <p className="text-[#728481]">{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-20 bg-[#484237] text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {companyInfo.stats.map((stat, index) => (
              <div
                key={index}
                className="text-center transform hover:scale-105 transition-transform duration-300"
              >
                <div className="text-5xl font-bold text-[#D7AC78] mb-2">
                  {stat.number}
                </div>
                <div className="text-base text-gray-300 font-medium">
                  {stat.label}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-br from-[#F8E6C6] to-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl md:text-5xl font-bold text-[#484237] mb-6">
            Let's Transform Your Project Together?
          </h2>
          <p className="text-xl text-[#728481] mb-8">
            Get in touch and discover how we can help make your dreams come true
          </p>
          <a
            href="/contato"
            className="inline-flex items-center px-8 py-4 bg-[#D7AC78] text-white font-medium rounded-lg hover:bg-[#c49a68] transform hover:scale-105 transition-all duration-300 shadow-lg hover:shadow-xl"
          >
            Schedule Consultation
          </a>
        </div>
      </section>
    </div>
  );
};

export default Sobre;
